<template>
  <div class="page">
    <initio-panel :hide-submit="true" @submit-handler="submitHandler" :errors="errors">
      <template v-slot:title>
        <h1>Make Payment</h1>
      </template>
      <template v-slot:before_form>

        <div class="info box" v-if="getChangelist() && (getChangelist().length !== 0)">
          <h3 class="title is-4 has-text-weight-normal">Here's what we are changing</h3>
          <div class="content">
            <ul>
              <li :key="question.name" v-for="question in getChangelist()">{{ question.name }} changed from <span class="old has-text-weight-bold">{{ question.before }}</span> to <span class="new has-text-weight-bold">{{ question.after }}</span></li>
            </ul>
          </div>
        </div>
        <div class="info box" v-else>
          <h3 class="title is-4 has-text-weight-normal">There are no changes to your cover</h3>
          <div class="content" v-if="answers[getStep('Vehicle.Quote')].vehicle_quote_adjustment_raw == 0">
            <p>Please <a v-on:click="back()" class="back" >go back</a> to make a change.</p>
          </div>
        </div>

        <div class="box">
          <h3 class="title is-4">Cover details</h3>
          <div class="columns is-multiline">
            <div class="column is-6">
              <p><strong>Insurer</strong></p>
              <p>Initio underwritten by <a href=" https://initio.co.nz/financial-strength/" target="_blank">IAG New Zealand Limited</a></p>
            </div>
            <div class="column is-6">
              <p><strong>Vehicle</strong></p>
              <ChosenVehicle />
            </div>
            <div v-if="answers[getStep('quote')].vehicle_cover_type !== 'third-party-only'" class="column is-6">
              <p><strong>Agreed Value</strong></p>
              <p>{{ answers[getStep('quote')].vehicle_sum_insured_amount | toCurrencyShort }} inc GST</p>
            </div>
            <div class="column is-6">
              <p><strong>Cover</strong></p>
              <p>{{ answerLabel(getStep('quote'), 'vehicle_cover_type') }}</p>
              <template v-if="answers[getStep('quote')].vehicle_cover_type == 'full'">
                <p>Nil-excess Windscreen &amp; Glass cover <span class='button button--feature is-primary is-small has-text-weight-bold'>INCLUDED</span></p>
                <p v-if="answerLabel(getStep('quote'),'vehicle_excess_under_25_note')">Cover for drivers under 25 years <span class='button button--feature is-primary is-small has-text-weight-bold'>INCLUDED</span></p>
                <p v-else>Cover for drivers under 25 years <span class='button button--feature is-danger is-small has-text-weight-bold'>EXCLUDED</span></p>
                <p v-if="answers[getStep('quote')].vehicle_low_usage == 'yes'"
                >Low use &lt; 5000km / year discount<span class='button button--feature is-primary is-small has-text-weight-bold'>INCLUDED</span></p>
                <p v-if="answers[getStep('Quote')].vehicle_include_roadside_assist_cover == 'yes'"
                    >Roadside Assist<span class='button button--feature is-primary is-small has-text-weight-bold'>INCLUDED</span></p>
                <p v-else>Roadside Assist<span class='button button--feature is-danger is-small has-text-weight-bold'>EXCLUDED</span></p>
              </template>
            </div>
            <div class="column is-6">
              <template v-if="answerLabel(getStep('quote'),'vehicle_excess_under_25_note')">
                <p><strong>Excesses</strong></p>
                <p><template v-if="answers[getStep('quote')].vehicle_excess">{{ answerLabel(getStep('quote'), 'vehicle_excess') }}</template><template v-else>{{ answerLabel(getStep('quote'), 'vehicle_excess_third_party') }}</template><br />
                  ${{ ( ( parseInt(answers[getStep('quote')].vehicle_excess) || parseInt(answers[getStep('quote')].vehicle_excess_third_party)) + 500 ) }} for under 25 drivers<br /><a href="#" @click.prevent="toggleModal()">Additional excesses may apply</a>
                  <modal
                      v-if="modalOpen"
                      :class="{ 'is-active': modalOpen }"
                      v-on:childToParent="toggleModal"
                      title="List of excesses"
                      footer="Summary of policy only. Please refer to the policy wording for full details of cover."
                  >
                    <InitioExcesses />
                  </modal>
                </p>
              </template>
              <template v-else>
                <p><strong>Excess</strong></p>
                <p><template v-if="answers[getStep('quote')].vehicle_excess">{{ answerLabel(getStep('quote'), 'vehicle_excess') }}</template><template v-else>{{ answerLabel(getStep('quote'), 'vehicle_excess_third_party') }}</template></p>
              </template>
            </div>
            <div class="column is-6" v-if="!isAltFlow">
              <p><strong>New {{ paymentInterval(true) }} Cost</strong></p>
              <p>{{ answers[getStep('quote')].vehicle_quote_premium_raw | toCurrency }} NZD, inc GST</p>
            </div>
          </div>
        </div>

        <div class="box">
            <h3 class="title is-4">Main Driver's details</h3>
            <div class="columns is-multiline">
                <div class="column" v-bind:class="[ item.title.substr(0,5) == 'Claim' ? 'is-12' : 'is-6' ]" v-for="item in drivers_details" :key="item.title">
                    <p><strong>{{ item.title }}</strong></p>
                    <template v-if="Array.isArray(item.value)">
                      <div class="mb-4" v-for="subitem in item.value" :key="subitem.index">
                        <div v-html="subitem"></div>
                      </div>
                    </template>
                    <template v-else>
                      <div v-html="item.value"></div>
                    </template>
                </div>
            </div>
        </div>
        
        <div class="box" v-if="answers[getStep('Vehicle.Quote')].vehicle_quote_adjustment_raw != 0 || ( getChangelist() && getChangelist().length !== 0)">
          <h3 class="title is-4">Payment details</h3>
          <template v-if="paymentInterval() == 'monthly'">
            <StripePayment v-on:success-response-data="setResponseParamsAndRedirectToSuccessPage" />
            <hr />
            <!-- Handle next billing cycle in StripePayment for alt or newbiz - always show next period amount -->
            <!-- Next period should dynamically have set the anniversary date based on the next period. ie this should handle mid-year changes and month 12 rate changes -->
            <div class="next-billing-cycle-info" v-if="answers[getStep('Vehicle.Quote')].vehicle_quote_premium_raw !== null && isAltFlow">
              Your revised {{ paymentInterval() }} payment of <strong>{{ answers[getStep('Vehicle.Quote')].vehicle_quote_next_period_premium_raw | toCurrency }}</strong> will next be charged on <strong>{{ fulltextDate(answers[getStep('Vehicle.Quote')].vehicle_quote_next_period_billing_date_sql)}}</strong>.
            </div>
          </template>
          <template v-if="paymentInterval() == 'yearly'">
            <AnnualPayment></AnnualPayment>
          </template>
        </div>

      </template>
      <template v-slot:sidebar>
        <initio-summary-payment class="info-box" />
        <disclosure-block />
      </template>
    </initio-panel>
  </div>
</template>

<script>

import InitioPanel from '@/components/initio/InitioPanel.vue'
import InitioSummaryPayment from '@/components/partials/SummaryPayment.vue'
import StripePayment from '@/components/partials/StripePayment.vue'
import stepSubmit from '@/mixins/stepSubmit.js'
import ChosenVehicle from '@/components/initio/formulate/ChosenVehicle.vue'
import { mapGetters } from 'vuex'
import { fulltextDate } from '@/helpers/dateHelper.js'
import { api } from '@/store/store.js';
import InitioExcesses from '@/components/partials/Excesses.vue'
import store from "../../../store/store";
import DisclosureBlock from "../../partials/DisclosureBlock";
import AnnualPayment from '@/components/partials/AnnualPayment.vue'
const axios = require("axios");

export default {
  components: {
    DisclosureBlock,
    InitioPanel,
    InitioSummaryPayment,
    StripePayment,
    AnnualPayment,
    //ChosenVehicle,
    InitioExcesses
  },
  mixins: [
    stepSubmit
  ],
  created () {
      this.$store.commit('updateSchema', []); // clear previous schema, as no schema for this page
  },
  mounted () {

      // we can check that we have data ready to go
      // this is at the start because it results in an axios call which will fail
      // then is caught by the axios interceptor and will redirect to error page
      // Store from previous quote-page ajax response to vue state

      const selected_driver = this.answers[this.getStep('Vehicle.Confirm')].vehicle_confirm_driver;

      if( this.client.drivers.find(element => element.driver_id === parseInt(selected_driver) )  ) {
        const existing_driver = this.client.drivers.find(element => element.driver_id === parseInt(selected_driver) ); // how do I stop the repeated question
        this.drivers_details = [
          { title: "Name of main driver", value: `${existing_driver.first_name} ${existing_driver.last_name}` },
          { title: "Birthdate", value: fulltextDate(existing_driver.dob) }
        ];
      }

  },
  methods: {
    fulltextDate,
    submitHandler() {
      let context = this;

      this.genericSubmitHandler()
        .then(response => {
          // console.log(response)
        }, error => {
          console.error("VehiclePayment Handler error", error); // Unsure if we need this or not, remove if not needed
          context.errors = error;
        });
    },
    toggleModal: function () {
        this.modalOpen = ! this.modalOpen;
    },
    getChangelist() {
      return this.$store.state.sessions[this.$store.state.session_id].backendData.changelist;
    },
    back: function() {
      this.currentSession.stepPath.pop();
      this.$router.push({name: this.currentSession.stepPath.slice(-1)[0] });
      //this.$router.go(-1);
    },
    setResponseParamsAndRedirectToSuccessPage(success_data){
      // Ideally we should be going through standard submit instead of manually adding data here
      let successPage = "VehicleAlt.Success";
      store.commit(
          'updateBackendData',
          {"VehicleAlt.Success": success_data}
      );
      store.getters.currentSession.stepPath.push(successPage);

      setTimeout(() => {
        // route to next page
        this.$router.push({name: successPage });
        // scroll to top
        window.scrollTo(0,0);
      }, 600); // Give some time to show a success message

    },
  },
  computed: {
    ...mapGetters({
      answers: 'flattenedAnswers',
      answerLabel: 'getAnswerLabel',
      getAnswer: 'getAnswer',
      sessionId: 'getSessionId',
      client: 'client',
      getStep: 'getStep',
      isAltFlow: 'isAltFlow',
      currentSession: 'currentSession',
      paymentInterval: 'getPaymentInterval',
    })
  },
  data () {
    return {
      errors: [],
      cover_details: [],
      drivers_details: [],
      modalOpen: false
    }
  }
}

</script>

<style scoped lang="scss">
  @import '~@/scss/partials/icons.scss';

  ::v-deep {
    .button {
      &--feature {
        padding: 0.1rem 0.4rem;
        height: auto;
        line-height: 0.8rem;
        vertical-align: middle;
        margin-top: -0.15rem;
        display: inline-block;
        margin-left: 0.4rem;
        pointer-events: none;

        &.is-primary {
          background: none;
          border: 1px solid $initio-green;
          color: $initio-green;
        }
        &.is-danger {
          background: none;
          border: 1px solid $red;
          color: $red;
        }
      }
    }

    div.column > .formulate-input-element {
      margin: 0;
      padding: 0;
    }

    .chosen-vehicle-block {
      padding: 0;
      background: none;
      border: none;
    }
  }

  .next-billing-cycle-info {
    font-size: 14px;
    color: $grey-text;
    strong {
      color: $grey-text;
    }
  }

  .box.info {
    background:$info-blue;
    border:none;
    font-size:(14rem / 16);

    ul {

      li {
        padding-left:0.3rem;

        &::before {
          content:"\2022";
          position:absolute;
          left:-1rem;
          font-size:1.2rem;
        }
      }
    }
  }

  .new {
    color:$initio-green;
  }
  .old {
    color:$red;
  }

</style>